import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import api from "../../lib/api";
import { Loading } from "../../components/States";
import { CHead, CCard, Pill } from "../../components/customer/ui";
import { toast } from "sonner";
import { AvailableAppointments } from '../../components/AvailableAppointments';
import { CalendarClock, User, MapPin, Plus } from "lucide-react";

const SERVICES = ["Breaker tripping", "Panel upgrade", "EV charger install", "Lighting / fixtures", "Outlet / switch repair", "Other"];

export default function CustomerAppointments() {
  const { data, loading, refetch } = useFetch("/appointments");
  const [service, setService] = useState(SERVICES[0]);
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    try {
      await api.post("/appointments", { service_type: service, notes });
      toast.success("Service requested! We'll confirm your appointment shortly.");
      setOpen(false); setNotes("");
      refetch();
    } catch { toast.error("Could not submit request"); }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <CHead
        title="Appointments"
        subtitle="Request service and track your scheduled visits."
        action={
          <button onClick={() => setOpen(!open)} className="flex items-center gap-2 rounded-xl bg-[#1286F2] px-5 py-3 font-semibold text-white hover:bg-[#0A84FF]" data-testid="request-service-btn">
            <Plus size={18} /> Request Service
          </button>
        }
      />

      <AvailableAppointments onBooked={refetch} />
      {open && (
        <CCard className="space-y-4 p-6">
          <form onSubmit={request} className="space-y-4" data-testid="request-form">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-600">What do you need help with?</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SERVICES.map((s) => (
                  <button type="button" key={s} onClick={() => setService(s)} className={`rounded-xl border px-4 py-3 text-sm font-medium transition ${service === s ? "border-[#1286F2] bg-blue-50 text-[#1286F2]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`} data-testid={`service-${s.slice(0, 6).replace(/\s/g, "-").toLowerCase()}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <textarea className="w-full rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" rows={3} placeholder="Anything else we should know?" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="request-notes" />
            <button className="w-full rounded-xl bg-[#1286F2] py-3 font-bold text-white hover:bg-[#0A84FF]" data-testid="request-submit">Submit Request</button>
          </form>
        </CCard>
      )}

      <div className="space-y-3">
        {(data || []).length === 0 && <p className="text-slate-400">No appointments yet. Request service to get started.</p>}
        {(data || []).map((a) => (
          <CCard key={a.id} className="p-6" data-testid={`c-appt-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="text-xl font-bold text-slate-900">{a.service_type}</div>
              <Pill status={a.status} />
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-slate-500">
              <div className="flex items-center gap-2"><CalendarClock size={15} /> {new Date(a.scheduled_date).toLocaleDateString()} · {a.window}</div>
              <div className="flex items-center gap-2"><User size={15} /> {a.technician}</div>
              <div className="flex items-center gap-2"><MapPin size={15} /> {a.address}</div>
            </div>
          </CCard>
        ))}
      </div>
    </div>
  );
}
